import { useEffect } from 'react';
import { Header } from '../../../components/feature/Header';
import { Footer } from '../../../components/feature/Footer';
import { AsIsVsRemodelHero } from './components/AsIsVsRemodelHero';
import { UnderstandingYourOptions } from './components/UnderstandingYourOptions';
import { TrueCostOfRemodeling } from './components/TrueCostOfRemodeling';
import { RentDictatesValue } from './components/RentDictatesValue';
import { HiddenCostsOfRemodeling } from './components/HiddenCostsOfRemodeling';
import { BenefitsOfSellingAsIs } from './components/BenefitsOfSellingAsIs';
import { WhyInvestorsBuyAsIs } from './components/WhyInvestorsBuyAsIs';
import { RealWorldCaseStudies } from './components/RealWorldCaseStudies';
import { WhenRemodelMakesSense } from './components/WhenRemodelMakesSense';
import { CommonRemodelMistakes } from './components/CommonRemodelMistakes';
import { OurAsIsProcess } from './components/OurAsIsProcess';
import { TimeValueOfMoney } from './components/TimeValueOfMoney';
import { StressAndLifestyleImpact } from './components/StressAndLifestyleImpact';
import { DecisionMakingFramework } from './components/DecisionMakingFramework';
import { AsIsVsRemodelFAQ } from './components/AsIsVsRemodelFAQ';
import { AsIsVsRemodelFinalCTA } from './components/AsIsVsRemodelFinalCTA';

export default function AsIsVsRemodelPage() {
  useEffect(() => {
    // Set document title
    document.title = 'Sell Apartment Building AS IS vs Remodel: Complete Guide & Cost Analysis';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Should you sell your apartment complex AS IS or remodel first? Discover why selling AS IS saves $500K-$2M+, eliminates stress, and closes in 7-30 days. Complete cost analysis, case studies, and expert guidance for apartment building owners.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Should you sell your apartment complex AS IS or remodel first? Discover why selling AS IS saves $500K-$2M+, eliminates stress, and closes in 7-30 days. Complete cost analysis, case studies, and expert guidance for apartment building owners.';
      document.head.appendChild(meta);
    }

    // Set meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'sell apartment building as is, apartment complex renovation costs, remodel before selling apartment, sell apartment as is vs renovate, apartment building remodeling expenses, sell multifamily property as is, apartment renovation return on investment');
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      metaKeywords.setAttribute('content', 'sell apartment building as is, apartment complex renovation costs, remodel before selling apartment, sell apartment as is vs renovate, apartment building remodeling expenses, sell multifamily property as is, apartment renovation return on investment');
      document.head.appendChild(metaKeywords);
    }

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://sellapartmentscash.com/sell-my-apartment-building/as-is-vs-remodel');
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', 'https://sellapartmentscash.com/sell-my-apartment-building/as-is-vs-remodel');
      document.head.appendChild(canonical);
    }

    // Set Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Sell Apartment Building AS IS vs Remodel: Complete Cost Analysis Guide');
    } else {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      ogTitle.setAttribute('content', 'Sell Apartment Building AS IS vs Remodel: Complete Cost Analysis Guide');
      document.head.appendChild(ogTitle);
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Comprehensive guide comparing selling your apartment complex AS IS versus remodeling first. Real cost analysis, case studies, and expert insights to help you make the right decision.');
    } else {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      ogDescription.setAttribute('content', 'Comprehensive guide comparing selling your apartment complex AS IS versus remodeling first. Real cost analysis, case studies, and expert insights to help you make the right decision.');
      document.head.appendChild(ogDescription);
    }

    // Add structured data for Article
    const articleStructuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Sell Apartment Building AS IS vs Remodel: Complete Guide & Cost Analysis",
      "description": "Comprehensive analysis of selling apartment buildings AS IS versus remodeling first, including cost breakdowns, case studies, and decision frameworks.",
      "author": {
        "@type": "Organization",
        "name": "Sell Apartments Cash"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Sell Apartments Cash",
        "logo": {
          "@type": "ImageObject",
          "url": "https://sellapartmentscash.com/logo.png"
        }
      },
      "datePublished": "2024-01-15",
      "dateModified": "2024-01-15"
    };

    // Add structured data for FAQ
    const faqStructuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Will I get less money if I sell my apartment building AS IS?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Not necessarily. While the offer price may be lower than a fully renovated property, you avoid spending $500K-$2M+ on renovations and carrying costs. In most cases, owners net MORE money by selling AS IS because they avoid the massive costs and risks of renovation."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to sell an apartment building AS IS?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "With professional investors, you can close in as little as 7-30 days. Compare that to 12-18 months if you renovate first, plus another 3-6 months to market and sell. Selling AS IS gets you cash in hand up to 18 months faster."
          }
        },
        {
          "@type": "Question",
          "name": "Why do renovation costs often exceed the increase in property value?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Commercial real estate is valued based on income, not renovation costs. Market rents dictate what investors will pay, regardless of how much you spend on upgrades. If your area can't support significantly higher rents, renovations won't increase value proportionally."
          }
        },
        {
          "@type": "Question",
          "name": "What condition does my apartment building need to be in to sell AS IS?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Any condition! Professional investors buy apartment buildings with deferred maintenance, structural issues, outdated units, problem tenants, code violations, and everything in between. The worse the condition, the more sense it makes to sell AS IS."
          }
        },
        {
          "@type": "Question",
          "name": "When does remodeling before selling actually make sense?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Remodeling makes sense only when: 1) Your market supports 20%+ rent increases, 2) You have $500K-$2M+ in liquid capital, 3) You can wait 12-18 months, 4) You have construction experience, and 5) The value increase will exceed costs by 50%+. Most owners can't meet all these criteria."
          }
        }
      ]
    };

    // Add structured data scripts
    let articleScript = document.querySelector('script[type="application/ld+json"][data-type="article"]');
    if (articleScript) {
      articleScript.textContent = JSON.stringify(articleStructuredData);
    } else {
      articleScript = document.createElement('script');
      articleScript.type = 'application/ld+json';
      articleScript.setAttribute('data-type', 'article');
      articleScript.textContent = JSON.stringify(articleStructuredData);
      document.head.appendChild(articleScript);
    }

    let faqScript = document.querySelector('script[type="application/ld+json"][data-type="faq"]');
    if (faqScript) {
      faqScript.textContent = JSON.stringify(faqStructuredData);
    } else {
      faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-type', 'faq');
      faqScript.textContent = JSON.stringify(faqStructuredData);
      document.head.appendChild(faqScript);
    }

    // Cleanup function
    return () => {
      const elementsToRemove = [
        document.querySelector('meta[name="description"]'),
        document.querySelector('meta[name="keywords"]'),
        document.querySelector('link[rel="canonical"]'),
        document.querySelector('meta[property="og:title"]'),
        document.querySelector('meta[property="og:description"]'),
        document.querySelector('script[type="application/ld+json"][data-type="article"]'),
        document.querySelector('script[type="application/ld+json"][data-type="faq"]')
      ].filter(element => {
        if (!element) return false;
        const content = element.getAttribute('content') || element.getAttribute('href') || element.textContent || '';
        return content.includes('as-is-vs-remodel') || content.includes('AS IS vs Remodel');
      });
      
      elementsToRemove.forEach(element => element?.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <AsIsVsRemodelHero />
      <UnderstandingYourOptions />
      <TrueCostOfRemodeling />
      <RentDictatesValue />
      <HiddenCostsOfRemodeling />
      <TimeValueOfMoney />
      <BenefitsOfSellingAsIs />
      <WhyInvestorsBuyAsIs />
      <RealWorldCaseStudies />
      <StressAndLifestyleImpact />
      <WhenRemodelMakesSense />
      <CommonRemodelMistakes />
      <DecisionMakingFramework />
      <OurAsIsProcess />
      <AsIsVsRemodelFAQ />
      <AsIsVsRemodelFinalCTA />
      <Footer />
    </div>
  );
}